/**
 * First-touch attribution for PostHog events.
 *
 * Why this exists: PostHog is initialized with `person_profiles: 'identified_only'`,
 * so anonymous visitors never get a person profile, which means `$initial_referrer`
 * and `$initial_utm_*` are not persisted for exactly the try-before-signup funnel we
 * need to measure. On top of that, the activation events (`anonymous_enhancement_completed`,
 * `enhancement_completed`) carried no origin at all, and `document.referrer` on /enhance
 * is usually the internal landing page rather than the real source.
 *
 * The fix is a first-touch record written on the visitor's first page load and read back
 * at activation time, so "this person came from X and ran an enhancement" is answerable
 * from a single event.
 *
 * UTM params are the primary signal because they survive in the URL; referrer is the
 * fallback and is frequently stripped by in-app browsers.
 */

const STORAGE_KEY = 'pmp_first_touch';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/**
 * Pull UTM params out of a query string. Returns only the keys actually present,
 * so an empty object means "no campaign params on this URL".
 */
function readUtmParams(search) {
    const params = new URLSearchParams(search || '');
    const found = {};

    UTM_KEYS.forEach(key => {
        const value = params.get(key);
        if (value) found[key] = value;
    });

    return found;
}

/**
 * Hostname of a referrer URL, or null when the referrer is absent or unparseable.
 */
function referringDomain(referrer) {
    if (!referrer) return null;

    try {
        return new URL(referrer).hostname;
    } catch (error) {
        return null;
    }
}

/**
 * Drop a leading `www.` so the apex and www hosts compare equal.
 */
function normalizeHost(hostname) {
    return (hostname || '').replace(/^www\./i, '').toLowerCase();
}

/**
 * True when the referrer is our own site: an internal navigation
 * (landing -> /enhance) that must not overwrite the real first touch.
 *
 * Compares on the normalized host, so www and apex are the same site. A hop
 * between two genuinely different hosts we own (e.g. the fly.dev origin and the
 * custom domain) would still read as external; no such navigation exists today.
 */
function isInternalReferrer(referrer) {
    const domain = referringDomain(referrer);
    return Boolean(domain) && normalizeHost(domain) === normalizeHost(window.location.hostname);
}

/**
 * Keep only the UTM keys present on an object.
 */
function pickUtms(source) {
    const found = {};

    UTM_KEYS.forEach(key => {
        if (source[key]) found[key] = source[key];
    });

    return found;
}

function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);

        // A truthy non-object payload would otherwise be treated as a valid record
        // and block every future write, wedging attribution permanently. Treat
        // anything that is not a plain object as absent so the next real visit wins.
        //
        // `at` is required as a shape marker: an empty object would otherwise pass
        // as a valid record and report as a genuine direct visit, which is exactly
        // the conflation the first_touch_recorded flag exists to prevent.
        const isRecord = parsed
            && typeof parsed === 'object'
            && !Array.isArray(parsed)
            && typeof parsed.at === 'string';

        return isRecord ? parsed : null;
    } catch (error) {
        // Private-mode / quota / malformed JSON. Attribution is best-effort.
        return null;
    }
}

function writeStored(record) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Record the visitor's first touch, once. Safe to call on every page load:
 * it no-ops when a first touch already exists, which is what keeps an internal
 * landing -> /enhance hop from clobbering the original source.
 *
 * Call this as early as possible on every entry point.
 *
 * @returns {object|null} the stored first-touch record
 */
export function captureFirstTouch() {
    const existing = readStored();
    if (existing) return existing;

    const referrer = document.referrer || '';
    const utm = readUtmParams(window.location.search);

    // Nothing worth recording yet: an internal hop carrying no campaign params
    // means this is not the visitor's real entry point.
    if (!Object.keys(utm).length && isInternalReferrer(referrer)) return null;

    const record = {
        referrer: referrer || 'direct',
        referring_domain: referringDomain(referrer) || 'direct',
        landing_path: window.location.pathname,
        at: new Date().toISOString(),
        ...utm
    };

    writeStored(record);
    return record;
}

/**
 * Attribution properties to spread into a PostHog capture.
 *
 * Campaign params live in two namespaces, and each means exactly one thing:
 *
 * - `utm_*`: the campaign that brought the visitor in (their first touch).
 *   Absent when the first touch carried no campaign.
 * - `session_utm_*`: the campaign on THIS visit. Always emitted when the current
 *   URL carries one, as a complete tuple.
 *
 * They are never merged and neither is ever filled from the other's source. The
 * one exception is when no first touch could be read at all: this visit is then
 * the only origin signal available, so it also populates `utm_*`, flagged by
 * `first_touch_recorded: false`.
 *
 * That strictness is the point. If `utm_*` were allowed to hold current-visit
 * values whenever the first touch happened to lack them, two visitors who clicked
 * the same link would report it under different property names depending on
 * history no analyst can see, so any campaign breakdown would over-count one
 * cohort and under-count the other.
 *
 * Two naming traps worth knowing before querying this:
 *
 * - `referrer` / `referring_domain` are the CURRENT page's referrer. On /enhance
 *   that is normally our own landing page, so breaking activation events down by
 *   `referring_domain` reports a self-referral. Use `first_touch_referring_domain`
 *   to answer "where did this person come from".
 * - `first_touch_recorded: false` means the first touch could not be READ (storage
 *   blocked, evicted, or corrupt). That is not the same as a direct visit, and the
 *   two must not be pooled: a private-mode visitor arriving from social would
 *   otherwise be counted as direct.
 */
export function getAttribution() {
    const firstTouch = readStored();
    const hasFirstTouch = Boolean(firstTouch);
    const currentUtm = readUtmParams(window.location.search);

    const props = {
        referrer: document.referrer || 'direct',
        referring_domain: referringDomain(document.referrer) || 'direct',
        first_touch_recorded: hasFirstTouch,
        first_touch_referrer: hasFirstTouch ? (firstTouch.referrer || 'direct') : 'unknown',
        first_touch_referring_domain: hasFirstTouch ? (firstTouch.referring_domain || 'direct') : 'unknown',
        first_touch_landing_path: hasFirstTouch ? (firstTouch.landing_path || null) : null,
        first_touch_at: hasFirstTouch ? (firstTouch.at || null) : null
    };

    // Each tuple is taken whole from ONE visit. Merging key by key would report a
    // source/medium/campaign combination that no real click ever produced.
    const firstTouchUtm = hasFirstTouch ? pickUtms(firstTouch) : {};

    if (Object.keys(firstTouchUtm).length) {
        Object.assign(props, firstTouchUtm);
    } else if (!hasFirstTouch) {
        // Nothing readable, so this visit is the only origin signal we have.
        Object.assign(props, currentUtm);
    }

    // The current visit's campaign, always complete and always under its own
    // prefix, never only the keys that happen to differ from the first touch.
    Object.keys(currentUtm).forEach(key => {
        props[`session_${key}`] = currentUtm[key];
    });

    return props;
}

/**
 * Clear the stored first touch. Exposed for tests and for resetting a browser
 * between manual attribution checks.
 */
export function resetFirstTouch() {
    try {
        localStorage.removeItem(STORAGE_KEY);
        return true;
    } catch (error) {
        return false;
    }
}

export const FIRST_TOUCH_STORAGE_KEY = STORAGE_KEY;
